<template>
  <init-loader v-if="skeletonLoading"></init-loader>
  <v-card v-else :loading="loading" ref="card">
    <v-alert
      v-if="!doc"
      prominent
      type="warning"
    >
      <v-row align="center">
        <v-col class="grow">페이지가 없습니다.</v-col>
        <v-col class="shrink" v-if="$store.state.fireUser">
          <v-btn @click="openDialog" color="primary">만들기</v-btn>
        </v-col>
      </v-row>
    </v-alert>
    <div v-else>
      <v-toolbar dense flat color="secondary" dark>
        <v-toolbar-title>
          {{head.title}}
        </v-toolbar-title>
        <v-spacer/>
        <v-btn icon color="" :to="`/${category}/index?category=${head.category}`"><v-icon>mdi-view-list</v-icon></v-btn>
        <v-btn icon color="" @click="openDialog" v-if="$store.state.fireUser"><v-icon>mdi-pencil</v-icon></v-btn>
        <v-btn icon color="" @click="remove" v-if="$store.state.fireUser"><v-icon>mdi-delete</v-icon></v-btn>
      </v-toolbar>
      <v-card-text>
        <v-subheader v-text="head.subtitle"/>
        <v-row dense align="start" justify="end">
          <v-col cols="4">
            <v-img :src="head.thumbnail" max-height="150" contain></v-img>
          </v-col>
          <v-col cols="8">
            <div style="white-space: pre-line">{{head.description}}</div>

            <div class="text-right font-italic mt-10" v-if="loading === false && head.countRead">읽음: {{head.countRead}}</div>
            <div class="text-right font-italic" v-if="loading === false && head.createdAt">작성일: <display-time :time="head.createdAt" /></div>
            <div class="text-right font-italic" v-if="loading === false && head.updatedAt">수정일: <display-time :time="head.updatedAt" /></div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider/>
      <v-card-text v-if="body.content">
        <viewer :initialValue="body.content" v-if="!dialog" :options="tuiOptions"></viewer>
      </v-card-text>
      <v-divider v-if="body.links.length"/>
      <v-card-text v-if="body.links.length">
        <v-subheader>참고 링크</v-subheader>
        <template v-for="(link, i) in body.links">
          <v-btn :key="i" label color="success" class="text-lowercase ma-2" :href="link" target="_blank">{{link}}</v-btn>
        </template>
      </v-card-text>
      <v-divider v-if="body.embeds.length"/>
      <v-card-text ref="youtube" v-if="body.embeds.length">
        <v-subheader>참고 영상</v-subheader>
        <template v-for="(embed, i) in body.embeds">
          <youtube :video-id="embed" :key="i" :player-width="playerSize.width" :player-height="playerSize.height"></youtube>
        </template>
      </v-card-text>
      <v-divider v-if="doc"/>
      <v-card-text v-if="doc">
        <v-row no-gutters>
          <v-col cols="4">
            <v-btn @click="toPage(-1)" block text color="primary"><v-icon x-large>mdi-menu-left</v-icon></v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn :to="`/${category}/index?category=${head.category}`" block text color="primary"><v-icon>mdi-view-list</v-icon></v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn @click="toPage(1)" block text color="primary"><v-icon x-large>mdi-menu-right</v-icon></v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider v-if="adsenseClient"/>
      <v-card-text v-if="adsenseClient">
        <InArticleAdsense
          :data-ad-client="adsenseClient">
        </InArticleAdsense>
      </v-card-text>
      <v-divider v-if="docId"/>
      <v-card-text v-if="docId">
        <comment :article="head" :id="docId" :refArticle="refArticle"/>
      </v-card-text>
    </div>
    <v-dialog v-model="dialog">
      <v-form v-model="valid" ref="form">
        <v-card :loading="loading">
          <v-card-title>
            작성
            <v-spacer/>
            <v-btn @click="save" icon><v-icon>mdi-content-save</v-icon></v-btn>
            <v-btn @click="dialog=false" icon><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pb-0">
            <v-row>
              <v-col cols="3">
                <v-text-field v-model="head.category" outlined label="종류" :rules="[rule.required, rule.maxLength(20)]" required clearable />
              </v-col>
              <v-col cols="9">
                <v-text-field v-model="head.title" outlined label="제목" :rules="[rule.required, rule.maxLength(80)]" required clearable />
              </v-col>
              <v-col cols="3">
                <v-file-input v-model="file" prepend-icon="" accept="image/*" label="사진 첨부" show-size outlined prepend-inner-icon="mdi-paperclip" @change="uploadThumbnail"></v-file-input>
              </v-col>
              <v-col cols="9">
                <v-text-field v-model="head.subtitle" outlined label="부제" :rules="[rule.required, rule.maxLength(100)]" required clearable />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="head.description" outlined label="설명" :rules="[rule.required, rule.maxLength(200)]" required clearable rows="2" />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider/>
          <v-card-text>
            <editor :initialValue="body.content" ref="editor" :options="tuiOptions" height="600px"></editor>
          </v-card-text>
          <v-divider/>
          <v-card-text class="pb-0">
            <v-subheader>이미지</v-subheader>
            <v-row dense>
              <template v-for="(image, i) in body.images">
                <v-col cols="12" sm="6" md="4" lg="3" :key="i">
                  <v-card height="200" class="pa-4">
                    <v-img
                      class="white--text align-start"
                      :src="image.thumbnail"
                      max-height="180"
                      contain
                    >
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on }">
                            <v-avatar color="primary">
                              <v-btn dark color="" v-clipboard="image.src" v-on="on" icon><v-icon>mdi-content-copy</v-icon></v-btn>
                            </v-avatar>
                          </template>
                          <span>이미지 경로를 복사하여 내용에 삽입합니다.</span>
                        </v-tooltip>
                        <v-avatar color="error" class="ml-2">
                          <v-btn dark color="" icon @click="itemRemove(body.images, i)"><v-icon>mdi-delete</v-icon></v-btn>
                        </v-avatar>
                      </v-card-actions>
                    </v-img>
                  </v-card>
                </v-col>
              </template>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-file-input
                  v-model="fileImage"
                  prepend-icon=""
                  accept="image/*"
                  label="사진 첨부"
                  show-size
                  outlined
                  prepend-inner-icon="mdi-paperclip"
                  @change="uploadImage"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text class="pb-0">
            <v-subheader>참고 링크</v-subheader>
            <v-row dense>
              <template v-for="(link, i) in body.links">
                <v-col cols="12" sm="6" md="4" lg="3" :key="i">
                  <v-text-field
                    v-model="body.links[i]"
                    outlined
                    append-icon="mdi-delete"
                    label="링크"
                    @click:append="itemRemove(body.links, i)"
                  />
                </v-col>
              </template>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-text-field
                  v-model="link"
                  outlined
                  append-icon="mdi-plus"
                  label="링크 추가"
                  placeholder="eg) https://xxx.com/yy/zz"
                  @click:append="linkAdd"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text class="pb-0">
            <v-subheader>참고 유튜브</v-subheader>
            <v-row dense>
              <template v-for="(embed, i) in body.embeds">
                <v-col cols="12" sm="6" md="4" lg="3" :key="i">
                  <v-text-field
                    v-model="body.embeds[i]"
                    outlined
                    append-icon="mdi-delete"
                    label="Youtube ID"
                    @click:append="itemRemove(body.embeds, i)"
                  />
                </v-col>
              </template>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-text-field
                  v-model="embed"
                  outlined
                  append-icon="mdi-plus"
                  label="Youtube ID 추가"
                  placeholder="eg) QNWX90Z-ytk"
                  @click:append="embedAdd"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>
    </v-dialog>
  </v-card>
</template>

<script>
import 'highlight.js/styles/github.css'
import InitLoader from './components/content-skeleton'
import { readAndCompressImage } from 'browser-image-resizer'
import pluralize from 'pluralize'
import delay from 'delay'
import DisplayTime from '@/components/display-time'
import Comment from '@/components/comment'
import codeSyntaxHighlight from '@toast-ui/editor-plugin-code-syntax-highlight'
import hljs from 'highlight.js/lib/highlight'
import javascript from 'highlight.js/lib/languages/javascript'
import bash from 'highlight.js/lib/languages/bash'
import dart from 'highlight.js/lib/languages/dart'
import vue from 'vue-highlight.js/lib/languages/vue'

hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('bash', bash)
hljs.registerLanguage('dart', dart)
hljs.registerLanguage('vue', vue)

export default {
  components: { InitLoader, DisplayTime, Comment },
  props: ['category', 'content'],
  data () {
    return {
      adsenseClient: process.env.VUE_APP_ADSENSE_CLIENT,
      tuiOptions: {
        language: 'ko',
        plugins: [[codeSyntaxHighlight, { hljs }]],
        linkAttribute: {
          target: '_blank'
        }
      },
      refHead: null,
      refBody: null,
      collection: pluralize(this.category),
      loading: false,
      head: {
        category: '',
        title: '',
        subtitle: '',
        description: '',
        thumbnail: '',
        createdAt: null,
        updatedAt: null,
        countComment: 0,
        countRead: 0,
        countLike: 0
      },
      body: {
        content: '',
        embeds: [],
        links: [],
        images: []
      },
      unsubscribeHead: null,
      unsubscribeBody: null,
      dialog: false,
      valid: false,
      embed: '',
      link: '',
      image: {
        thumbnail: '',
        src: ''
      },
      file: null,
      progress: 0,
      skeletonLoading: false,
      playerSize: { width: 640, height: 360 },
      rule: {
        required: v => !!v || '필수 항목입니다.',
        minLength: length => v => v.length >= length || `${length}자리 이상으로 입력하세요.`,
        maxLength: length => v => v.length <= length || `${length}자리 이하으로 입력하세요.`
      },
      refArticle: null,
      fileImage: null,
      copiedURL: '',
      doc: null,
      docId: null
    }
  },
  computed: {
    getSize () {
      if (!this.$refs.youtube) return { width: 640, height: 360 }
      // console.log({ width: this.$refs.youtube.$el.clientWidth, height: 9 * this.$refs.youtube.$el.clientWidth / 16 })
      return { width: this.$refs.youtube.$el.clientWidth, height: 9 * this.$refs.youtube.$el.clientWidth / 16 }
    }
  },
  created () {
    this.subscribe()
  },
  mounted () {
    // this.$refs.footer.$el.clientHeight
    // this.playerWidth = this.$refs.youtube.$el.clientWidth
    // console.log(this.$refs.youtube)

  },
  destroyed () {
    if (this.unsubscribeHead) this.unsubscribeHead()
    if (this.unsubscribeBody) this.unsubscribeBody()
  },
  watch: {
    content (n, o) {
      this.subscribe()
    }
  },
  methods: {
    onError (e) {
      this.loading = false
      this.$toast.error(e.message)
    },
    itemRemove (items, i) {
      items.splice(i, 1)
    },
    itemAdd (items, item) {
      if (items.length >= 4) throw Error('4개 까지만 등록됩니다')
      if (item.length < 7) throw Error('길이가 너무 작습니다')

      items.push(item)
      // item = ''
      this.link = ''
      this.embed = ''
    },
    linkAdd () {
      if (this.body.links.length >= 4) throw Error('4개 까지만 등록됩니다')
      if (this.link.length < 7) throw Error('길이가 너무 작습니다')
      this.body.links.push(this.link)
      this.link = ''
    },
    embedAdd () {
      if (this.body.embeds.length >= 4) throw Error('4개 까지만 등록됩니다')
      if (this.embed.length !== 11) throw Error('11자만 가능합니다')
      this.body.embeds.push(this.embed)
      this.embed = ''
    },
    clear () {
      this.head.category = ''
      this.head.title = ''
      this.head.subtitle = ''
      this.head.description = ''
      this.head.thumbnail = ''
      this.head.createdAt = null
      this.head.updatedAt = null
      this.head.countComment = 0
      this.head.countRead = 0
      this.head.countLike = 0
      this.body.content = ''
      this.body.embeds = []
      this.body.links = []
      this.body.images = []
    },
    initLoad () {
      this.skeletonLoading = true
      delay(500).then(() => {
        this.skeletonLoading = false
      })
      delay(1000).then(() => {
        if (!this.$refs.card) return
        const width = this.$refs.card.$el.clientWidth - 36
        this.playerSize = { width: width, height: 9 * width / 16 }
      })
    },
    async subscribe () {
      this.clear()
      this.initLoad()
      this.loading = true

      this.refArticle = this.$firebase.firestore().collection(this.collection)
      this.refHead = this.$firebase.firestore()
        .collection(this.collection)
        .doc(this.content)
      this.refBody = this.$firebase.firestore()
        .collection(this.collection)
        .doc(this.content)
        .collection('body')
        .doc('last')
      await this.refHead.update({ countRead: this.$firebase.firestore.FieldValue.increment(1) })
      this.unsubscribeHead = this.refHead.onSnapshot((doc) => {
        this.loading = false
        // this.skeletonLoading = false
        if (!doc.exists) return
        this.doc = doc
        const item = doc.data()
        this.docId = doc.id
        item.createdAt = item.createdAt.toDate()
        item.updatedAt = item.updatedAt.toDate()
        this.head = item
      }, this.onError)
      this.unsubscribeBody = this.refBody.onSnapshot((doc) => {
        this.loading = false
        if (!doc.exists) return
        this.body = doc.data()
        // console.log(this.body)
      }, this.onError)
    },
    openDialog () {
      this.dialog = true
      if (this.$refs.form) this.$refs.form.resetValidation()
    },
    async save () {
      if (!this.$refs.form.validate()) return
      this.head.uid = this.$store.state.fireUser.uid
      this.body.uid = this.$store.state.fireUser.uid
      this.body.content = this.$refs.editor.invoke('getMarkdown')
      if (!this.body.content.length) throw Error('내용을 채워주세요')
      if (this.body.content.length > 10000) throw Error('10000자 이상은 허용하지 않습니다.')

      this.loading = true
      try {
        if (!this.head.createdAt) this.head.createdAt = new Date()
        this.head.updatedAt = new Date()
        await this.refHead.set(this.head)
        await this.refBody.set(this.body)
      } finally {
        this.dialog = false
        this.loading = false
        this.subscribe()
      }
    },
    async remove () {
      const r = await this.$swal.fire({
        title: '정말 삭제하시겠습니까?',
        text: '삭제 후 되돌릴 수 없습니다.',
        icon: 'error',
        // confirmButtonText: 'Cool',
        showCancelButton: true
      })
      if (!r.value) return
      this.loading = true
      try {
        // await this.refBody.delete()
        await this.refHead.delete()
      } finally {
        this.loading = false
        this.$router.push('/')
      }
    },
    async uploadThumbnail () {
      if (!this.file) return
      this.loading = true
      const ref = this.$firebase.storage().ref()
      const config = {
        quality: 0.5,
        maxWidth: 300,
        maxHeight: 300,
        autoRotate: true
      }
      try {
        let image = this.file
        if (this.file.size > 1024 * 1024) image = await readAndCompressImage(this.file, config)
        const fileName = `${this.content}-thumbnail`
        const task = ref.child('images').child(this.collection).child(fileName).put(image)
        task.on(this.$firebase.storage.TaskEvent.STATE_CHANGED, (sn) => {
          this.progress = (sn.bytesTransferred / sn.totalBytes) * 100
          switch (sn.state) {
            case this.$firebase.storage.TaskState.PAUSED: // or 'paused'
              console.log('Upload is paused')
              break
            case this.$firebase.storage.TaskState.RUNNING: // or 'running'
              console.log('Upload is running')
              break
            case this.$firebase.storage.TaskState.PROGRESS: // or 'PROGRESS'
              console.log('Upload is progress')
              break
          }
        }, (err) => {
          this.$toast.error(err.message)
        }, () => {
          task.snapshot.ref.getDownloadURL()
            .then((url) => {
              this.head.thumbnail = url
            })
        })
      } finally {
        this.loading = false
      }
    },
    async uploadImage () {
      if (!this.fileImage) return
      this.loading = true
      const ref = this.$firebase.storage().ref()
      const config = {
        quality: 0.5,
        maxWidth: 300,
        maxHeight: 300,
        autoRotate: true
      }
      try {
        const image = {
          thumbnail: '',
          src: ''
        }
        let thumbnail = this.fileImage
        if (this.fileImage.size > 1024 * 64) thumbnail = await readAndCompressImage(this.fileImage, config)
        const thumbnailName = `${this.content}-${this.body.images.length}-thumbnail`
        const taskThumbnail = ref.child('images').child(this.collection).child(thumbnailName).put(thumbnail)
        taskThumbnail.on(this.$firebase.storage.TaskEvent.STATE_CHANGED, (sn) => {
          this.progress = (sn.bytesTransferred / sn.totalBytes) * 100
          switch (sn.state) {
            case this.$firebase.storage.TaskState.PAUSED: // or 'paused'
              console.log('Upload is paused')
              break
            case this.$firebase.storage.TaskState.RUNNING: // or 'running'
              console.log('Upload is running')
              break
            case this.$firebase.storage.TaskState.PROGRESS: // or 'PROGRESS'
              console.log('Upload is progress')
              break
          }
        }, (err) => {
          this.$toast.error(err.message)
        }, () => {
          taskThumbnail.snapshot.ref.getDownloadURL()
            .then((url) => {
              image.thumbnail = url
              if (image.src) this.body.images.push(image)
            })
        })
        const srcName = `${this.content}-${this.body.images.length}`
        const task = ref.child('images').child(this.collection).child(srcName).put(this.fileImage)
        task.on(this.$firebase.storage.TaskEvent.STATE_CHANGED, (sn) => {
          this.progress = (sn.bytesTransferred / sn.totalBytes) * 100
          switch (sn.state) {
            case this.$firebase.storage.TaskState.PAUSED: // or 'paused'
              console.log('Upload is paused')
              break
            case this.$firebase.storage.TaskState.RUNNING: // or 'running'
              console.log('Upload is running')
              break
            case this.$firebase.storage.TaskState.PROGRESS: // or 'PROGRESS'
              console.log('Upload is progress')
              break
          }
        }, (err) => {
          this.$toast.error(err.message)
        }, () => {
          task.snapshot.ref.getDownloadURL()
            .then((url) => {
              image.src = url
              if (image.thumbnail) this.body.images.push(image)
            })
        })
      } finally {
        this.loading = false
      }
    },
    async toPage (arrow) {
      if (!this.doc) return
      const ref = this.$firebase.firestore().collection(this.collection)
        .where('category', '==', this.head.category).orderBy('createdAt')
      let sn
      if (arrow < 0) sn = await ref.endBefore(this.doc).limitToLast(1).get()
      else sn = await ref.startAfter(this.doc).limit(1).get()

      if (sn.empty) return this.$toast.info('더이상 페이지가 없습니다')
      const doc = sn.docs[0]
      this.$router.push(`/${this.category}/${doc.id}`)
    }
  }
}
</script>

<style lang="sass">
.v-application
  // .v-text-field
  //   border-radius: 0
  code, kbd
    display: inline-block
    border-radius: 0 // $code-kbd-border-radius
    white-space: pre-wrap
    // font-size: $code-kbd-font-size
    // font-weight: $code-kbd-font-weight
    box-shadow: none

    &:after,
    &:before
      content: ""
      letter-spacing: 0px

  code
    background-color: transparent
    color: black

  kbd
    background-color: transparent
    color: black
</style>
